import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog, ErrorNotice, Field, Panel, Tabs, TextArea, TextInput } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  OUTREACH_QUERY_KEYS,
  OUTREACH_TEMPLATE_LIMITS,
  OUTREACH_TEMPLATE_SCOPE,
  SIGNATURE_QUERY_KEY,
  createTemplate,
  insertAt,
  placeholderToken,
  readSignature,
  removeTemplate,
  updateTemplate,
  type OutreachPlaceholder,
  type OutreachTemplate,
  type OutreachTemplateInput,
  type OutreachTemplateScope,
} from '@/lib/outreach-templates';
import { PlaceholderPicker } from './PlaceholderPicker';
import { TemplatePreview } from './TemplatePreview';

/** Writing the template, or reading it as a client would. */
const FORM_VIEW = { draft: 'draft', preview: 'preview' } as const;
type FormView = (typeof FORM_VIEW)[keyof typeof FORM_VIEW];

/** The two fields a placeholder can go into. */
const TEXT_FIELD = { subject: 'subject', body: 'body' } as const;
type TextField = (typeof TEXT_FIELD)[keyof typeof TEXT_FIELD];
/** Each field's name as the picker's hint says it, matching the field labels. */
const TEXT_FIELD_NAME: Readonly<Record<TextField, string>> = { [TEXT_FIELD.subject]: 'subject', [TEXT_FIELD.body]: 'email' };

export interface TemplateFormProps {
  readonly scope: OutreachTemplateScope;
  /** The template being edited; null for a new one. */
  readonly template: OutreachTemplate | null;
  readonly placeholders: readonly OutreachPlaceholder[];
  /** After a new template is added: the page moves to it. */
  readonly onCreated: (template: OutreachTemplate) => void;
  /** After the template is removed, or on Cancel: back to the list. */
  readonly onDone: () => void;
}

/**
 * Adding or editing a template on its own page: the fields on the left, the
 * placeholder picker above the subject and email it inserts into, and a live
 * preview with sample values on the right. Saving an edit stays on the page
 * and says Saved; adding one moves to the new template's page.
 *
 * @param props - The scope, the template (or null), the placeholders and the handlers.
 * @returns The form.
 */
export function TemplateForm({ scope, template, placeholders, onCreated, onDone }: TemplateFormProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = useState(template?.name ?? '');
  const [whenToUse, setWhenToUse] = useState(template?.whenToUse ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [saved, setSaved] = useState<OutreachTemplate | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [target, setTarget] = useState<TextField>(TEXT_FIELD.body);
  const [view, setView] = useState<FormView>(FORM_VIEW.draft);
  const signature = useQuery({ queryKey: SIGNATURE_QUERY_KEY, queryFn: ({ signal }) => readSignature(signal), staleTime: 10 * 60_000 });
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  // After a save, edits go against the saved version, not the one first opened.
  const current = saved ?? template;
  const input: OutreachTemplateInput = { name: name.trim(), whenToUse: whenToUse.trim(), subject: subject.trim(), body: body.trim() };
  const complete = input.name !== '' && input.subject !== '' && input.body !== '';
  const changed =
    current === null ||
    input.name !== current.name ||
    input.whenToUse !== current.whenToUse ||
    input.subject !== current.subject ||
    input.body !== current.body;
  const queryKey = scope === OUTREACH_TEMPLATE_SCOPE.team ? OUTREACH_QUERY_KEYS.team : OUTREACH_QUERY_KEYS.mine;

  const save = useMutation({
    mutationFn: () => (current === null ? createTemplate(scope, input) : updateTemplate(current, input)),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey });
      if (current === null) onCreated(result);
      else setSaved(result);
    },
  });
  const remove = useMutation({
    mutationFn: () => (current === null ? Promise.resolve() : removeTemplate(current)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      onDone();
    },
  });

  const insert = (key: string) => {
    const token = placeholderToken(key);
    const field = target === TEXT_FIELD.subject ? subjectRef.current : bodyRef.current;
    const value = target === TEXT_FIELD.subject ? subject : body;
    const next = insertAt(value, token, { start: field?.selectionStart ?? null, end: field?.selectionEnd ?? null });
    if (target === TEXT_FIELD.subject) setSubject(next.value);
    else setBody(next.value);
    // Put the caret after the placeholder once React has written the new value.
    requestAnimationFrame(() => {
      field?.focus();
      field?.setSelectionRange(next.caret, next.caret);
    });
  };

  return (
    <div>
      <Panel className="space-y-5 p-6">
        <Tabs
          label="Write or preview"
          active={view}
          onChange={setView}
          items={[
            { key: FORM_VIEW.draft, label: 'Draft' },
            { key: FORM_VIEW.preview, label: 'Preview' },
          ]}
        />
        {view === FORM_VIEW.preview ? (
          <TemplatePreview subject={subject} body={body} placeholders={placeholders} signature={signature.data} />
        ) : (
        <>
        <Field label="Name" htmlFor="outreach-template-name" required hint="For you: how you'll find it in the list.">
          <TextInput
            id="outreach-template-name"
            maxLength={OUTREACH_TEMPLATE_LIMITS.name}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field
          label="When to use it"
          htmlFor="outreach-template-when"
          hint="One line the assistant matches clients against, like “booked last year and was happy”."
        >
          <TextInput
            id="outreach-template-when"
            maxLength={OUTREACH_TEMPLATE_LIMITS.whenToUse}
            value={whenToUse}
            onChange={(event) => setWhenToUse(event.target.value)}
          />
        </Field>

        <PlaceholderPicker placeholders={placeholders} target={TEXT_FIELD_NAME[target]} onInsert={insert} />

        <Field label="Subject" htmlFor="outreach-template-subject" required>
          <TextInput
            id="outreach-template-subject"
            ref={subjectRef}
            maxLength={OUTREACH_TEMPLATE_LIMITS.subject}
            value={subject}
            onFocus={() => setTarget(TEXT_FIELD.subject)}
            onChange={(event) => setSubject(event.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor="outreach-template-body" required hint="Plain text. Your Salesforce signature is added under it.">
          <TextArea
            id="outreach-template-body"
            ref={bodyRef}
            rows={14}
            maxLength={OUTREACH_TEMPLATE_LIMITS.body}
            value={body}
            onFocus={() => setTarget(TEXT_FIELD.body)}
            onChange={(event) => setBody(event.target.value)}
          />
        </Field>
        </>
        )}

        {save.isError ? <ErrorNotice inline title="Template not saved" message={errorMessage(save.error)} /> : null}

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-portal-line pt-5">
          {current === null ? null : (
            <Button variant="ghost" className="mr-auto" onClick={() => setConfirmRemove(true)}>
              Remove template
            </Button>
          )}
          {saved !== null && !changed ? <span className="text-portal-micro font-bold text-emerald-600">Saved</span> : null}
          <Button variant="secondary" onClick={onDone}>
            {current === null ? 'Cancel' : 'Back to templates'}
          </Button>
          <Button disabled={!complete || !changed || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : current === null ? 'Add template' : 'Save changes'}
          </Button>
        </div>
      </Panel>


      <ConfirmDialog
        open={confirmRemove}
        title="Remove this template?"
        message={
          scope === OUTREACH_TEMPLATE_SCOPE.team
            ? 'Reps stop seeing it. Copies reps already made stay theirs.'
            : 'Drafts for your clients stop using it.'
        }
        confirmLabel="Remove"
        tone="danger"
        pending={remove.isPending}
        {...(remove.isError ? { error: errorMessage(remove.error) } : {})}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}
